/* eslint-disable no-console */
import type {InternalAxiosRequestConfig, AxiosInstance, AxiosResponse, AxiosError} from 'axios';
import {AxiosHeaders} from 'axios';
import {camelizeKeys, decamelizeKeys, HumpsProcessor} from 'humps';

import type {FirebaseTokenProvider} from 'services/Firebase/FirebaseTokenProvider';
import {TasksObserver} from 'services/TasksObserver/TasksObserver';
import store from 'store';
import {getActiveProjectFromLocalStorage} from 'store/profile/utils';

import {accessControl} from './accesscontrol';

interface MetaConfig<D> extends InternalAxiosRequestConfig<D> {
  metadata: {
    startTime: number;
  };
}
const LOGGER_ENABLED = process.env.LOG_API === 'true';

// Logger configuration
const logger = {
  colors: {
    request: '#2196F3', // blue
    response: '#4CAF50', // green
    error: '#F44336', // red
    warning: '#FFC107', // amber
    info: '#9E9E9E', // grey
  },
  groupCollapsed: (title: string, color: string, ...args: any[]) => {
    console.groupCollapsed(`%c${title}`, `color: ${color}; font-weight: bold`, ...args);
  },
  log: (title: string, color: string, data: any) => {
    console.log(`%c${title}`, `color: ${color}; font-weight: bold`, data);
  },
};

// Create logging interceptors
const createLoggingInterceptors = () => {
  const requestInterceptor = (config: InternalAxiosRequestConfig) => {
    logger.groupCollapsed('🌐 API Request', logger.colors.request, config.url);
    logger.log('URL', logger.colors.info, `${config.method?.toUpperCase()} ${config.url}`);
    logger.log('Headers', logger.colors.info, config.headers);
    if (config.params) logger.log('Query Params', logger.colors.info, config.params);
    if (config.data) logger.log('Request Data', logger.colors.info, config.data);
    console.groupEnd();
    return config;
  };

  const responseInterceptor = <T, D>(response: AxiosResponse<T, D>) => {
    const config = response.config as MetaConfig<D>;
    const duration = config.metadata ? new Date().getTime() - config.metadata.startTime : 0;

    logger.groupCollapsed('✅ API Response', logger.colors.response, response.config.url);
    logger.log('Status', logger.colors.info, `${response.status} ${response.statusText}`);
    logger.log('Duration', logger.colors.info, `${duration}ms`);
    logger.log('Headers', logger.colors.info, response.headers);
    logger.log('Data', logger.colors.info, response.data);
    console.groupEnd();
    return response;
  };

  const errorInterceptor = (error: AxiosError) => {
    logger.groupCollapsed('❌ API Error', logger.colors.error, error.config?.url);
    logger.log('Status', logger.colors.error, error.response?.status);
    logger.log('Message', logger.colors.error, error.message);
    if (error.response?.data) logger.log('Response Data', logger.colors.error, error.response.data);
    console.groupEnd();
    return Promise.reject(error);
  };

  return {requestInterceptor, responseInterceptor, errorInterceptor};
};

export const camelCaseConverter: HumpsProcessor = (key, convert, options) => {
  if (key.includes('-')) return key;
  return convert(key, options);
};

export const createAuthInterceptor = (tokenProvider: FirebaseTokenProvider) => {
  return async (config: InternalAxiosRequestConfig) => {
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }

    const token = await tokenProvider.getUserIdToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers['X-Journey-Platform'] = window.location.pathname.includes('signup/alt-a') ? 'c4_konvert' : 'c4';

    return config;
  };
};

const createRequestTransformInterceptor = () => {
  return (config: InternalAxiosRequestConfig) => {
    if (
      config.headers['Content-Type'] &&
      typeof config.headers['Content-Type'] === 'string' &&
      config.headers['Content-Type'].includes('multipart/form-data')
    ) {
      return config;
    }

    config.params = config.params && decamelizeKeys(config.params, camelCaseConverter);
    config.data = config.data && decamelizeKeys(config.data, camelCaseConverter);
    return config;
  };
};

const createResponseTransformInterceptor = () => {
  return (response: AxiosResponse) => {
    if (response.config.disableCamelCaseConverter) {
      return response;
    }

    if (response.data && response.headers['content-type']?.includes('application/json')) {
      if (
        !response.config.ignoreAffectedTaskIds &&
        Array.isArray(response.data?.affected_task_ids) &&
        response.data.affected_task_ids.length > 0
      ) {
        const state = store.getState();
        TasksObserver.getInstance().load({
          ids: response.data.affected_task_ids,
          projectId: getActiveProjectFromLocalStorage(state.profile.worker?.id ?? '', state.profile.activeCompanyId),
        });
      }

      let savedSamples: any[] = [];
      if (response.config.url?.includes('prep/status') && response.data?.result?.samples) {
        savedSamples = [...response.data.result.samples];
      }

      response.data = camelizeKeys(response.data, camelCaseConverter);

      if (savedSamples.length) {
        response.data.result.samples = savedSamples;
      }
    }
    return response;
  };
};

const createErrorInterceptor = () => {
  return (error: AxiosError) => {
    if (error.response?.status === 403) {
      accessControl.handleAccessDenied();
    }
    return Promise.reject(error);
  };
};

export const setupInterceptors = (instance: AxiosInstance, tokenProvider: FirebaseTokenProvider): void => {
  // development only logging
  // npm start:debug
  if (LOGGER_ENABLED && process.env.NODE_ENV === 'development') {
    const {requestInterceptor, responseInterceptor, errorInterceptor} = createLoggingInterceptors();
    instance.interceptors.request.use(requestInterceptor);
    instance.interceptors.response.use(responseInterceptor, errorInterceptor);
  }

  // Your existing interceptors
  accessControl.setAxiosInstance(instance);

  instance.interceptors.request.use(createAuthInterceptor(tokenProvider), (error: AxiosError) => Promise.reject(error));

  instance.interceptors.request.use(createRequestTransformInterceptor(), (error: AxiosError) => Promise.reject(error));

  instance.interceptors.response.use(createResponseTransformInterceptor(), createErrorInterceptor());
};
